import React, { useEffect, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { Add, FlashOn, ShoppingCartOutlined } from '@mui/icons-material';
import api from '../../../../api/axios';
import CreateBuyOrderModal from './CreateBuyOrderModal';
import { useCurrency } from '../../../../hooks/useCurrency';
import { moneyFromMinor } from '../../../utils/UnitConversion.utils';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';

// Matches BuyOrderGroupDTO
interface BuyOrderGroupDTO {
  priceMinor: number;
  quantity: number;
}

// Matches BuyOrderResponseDTO from Kotlin
interface BuyOrderResponseDTO {
  referencePriceMinor: number | null;
  orders: BuyOrderGroupDTO[];
}

interface Props {
  marketHashName?: string;
  referencePriceMinor?: number;
}

const BuyOrderList: React.FC<Props> = ({ marketHashName, referencePriceMinor: propsReferencePrice }) => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  const { convertFromUSD, formatAmountWithoutCurrency } = useCurrency();
  const currencySymbol = useSelector((state: RootState) => state.currency.symbol);

  const [loading, setLoading] = useState(false);
  const [marketData, setMarketData] = useState<BuyOrderResponseDTO>({
    referencePriceMinor: null,
    orders: []
  });
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const fetchOrders = async () => {
    if (!marketHashName) return;
    setLoading(true);
    try {
      const endpoint = isAuthenticated ? '/buy-orders' : '/guest/buy-orders';
      const { data } = await api.get<BuyOrderResponseDTO>(endpoint, {
        params: { itemName: marketHashName }
      });
      setMarketData(data || { referencePriceMinor: null, orders: [] });
    } catch (err) {
      console.error('Failed to fetch buy orders', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [marketHashName]);

  return (
      <div className="flex flex-col h-full gap-4">
        {/* Main List Container - Fixed Radius to 0 */}
        <div className="flex-1 rounded bg-neutral-900/40 border border-neutral-800/60 overflow-hidden flex flex-col">

          {/* Header section integrated into the box */}
          <div className="flex items-center justify-between p-3 border-b border-neutral-800 bg-black/20">
            <div className="flex items-center gap-2 text-neutral-400">
              <FlashOn className="text-amber-500" sx={{ fontSize: 16 }} />
              <span className="font-bold text-[11px] uppercase tracking-[0.15em]">Buy Orders</span>
            </div>
            {isAuthenticated && (
                <Button
                    size="small"
                    onClick={() => setCreateModalOpen(true)}
                    // Set borderRadius to 0 for flat design
                    className="!min-w-0 !p-1.5 !rounded !bg-neutral-800/50 !text-neutral-400 hover:!text-white hover:!bg-neutral-800 transition-all border border-neutral-700/50"
                >
                  <Add sx={{ fontSize: 18, color: '#3f51b5' }} />
                </Button>
            )}
          </div>

          <div className="grid grid-cols-2 px-3 py-2 text-[10px] uppercase tracking-wider text-neutral-500 font-bold border-b border-neutral-800 bg-black/10">
            <span>Price ({currencySymbol})</span>
            <span className="text-right">Quantity</span>
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-neutral-800 hover:scrollbar-thumb-neutral-700">
            {loading ? (
                <div className="h-full flex items-center justify-center">
                  <CircularProgress size={20} className="text-neutral-600" />
                </div>
            ) : marketData.orders.length > 0 ? (
                marketData.orders.slice(0, 15).map((order, i) => (
                    <div
                        key={i}
                        className="grid grid-cols-2 px-3 py-2.5 text-xs border-b border-neutral-800/30 items-center hover:bg-white/[0.02] transition-colors"
                    >
                      <span className="font-semibold text-neutral-200">
                        {formatAmountWithoutCurrency(convertFromUSD(moneyFromMinor(order.priceMinor)))}
                      </span>
                      <span className="text-right text-neutral-400 font-medium">{order.quantity}</span>
                    </div>
                ))
            ) : (
                <div className="h-full flex flex-col items-center justify-center p-6 opacity-20">
                  <ShoppingCartOutlined sx={{ fontSize: 32, mb: 1, color: 'white' }} />
                  <span className="text-[10px] uppercase font-bold tracking-tighter text-white text-center">
                    No Active Orders
                  </span>
                </div>
            )}
          </div>
        </div>

        <CreateBuyOrderModal
            open={createModalOpen}
            onClose={() => setCreateModalOpen(false)}
            onSuccess={fetchOrders}
            itemName={marketHashName || ''}
            // Use backend reference price if available, otherwise fallback to props
            referencePriceMinor={marketData.referencePriceMinor ?? propsReferencePrice}
        />
      </div>
  );
};

export default BuyOrderList;